import { z } from 'zod'

// ─── Schemas ─────────────────────────────────────────────────────────────────

export const quizOptionSchema = z.object({
  label: z.string(),
  text: z.string(),
  correct: z.boolean(),
})

export const quizDataSchema = z.object({
  type: z.literal('quiz'),
  question: z.string(),
  options: z.array(quizOptionSchema),
})

export const learningSessionSchema = z.object({
  id: z.uuid(),
  courseId: z.uuid(),
  userId: z.uuid(),
  status: z.enum(['active', 'paused', 'ended']),
  language: z.string(),
  messageCount: z.number(),
  totalTokens: z.number(),
  lastActivityAt: z.string().nullable(),
  startedAt: z.string().nullable(),
  endedAt: z.string().nullable(),
  createdAt: z.string(),
})

export const sessionMessageSchema = z.object({
  id: z.string(),
  sessionId: z.string(),
  role: z.enum(['user', 'assistant', 'system']),
  content: z.string(),
  messageUuid: z.string(),
  tokensUsed: z.number(),
  metadata: z
    .object({
      quiz: quizDataSchema.optional(),
      sources: z.array(z.string()).optional(),
    })
    .nullable(),
  createdAt: z.string(),
})

// ─── Types ────────────────────────────────────────────────────────────────────

export type QuizOption = z.infer<typeof quizOptionSchema>
export type QuizData = z.infer<typeof quizDataSchema>
export type LearningSession = z.infer<typeof learningSessionSchema>
export type SessionMessage = z.infer<typeof sessionMessageSchema>

/** Transient message during SSE streaming — never persisted */
export interface StreamingMessage {
  role: 'assistant'
  content: string
  isStreaming: boolean
}

// ─── Exercise schemas ─────────────────────────────────────────────────────────

export const exerciseMcqSchema = z.object({
  type: z.literal('mcq'),
  question: z.string(),
  options: z.array(z.string()),
  correct: z.array(z.number()),
  multiple: z.boolean().default(false),
  explanation: z.string().optional(),
})

export const exerciseOpenSchema = z.object({
  type: z.literal('open'),
  question: z.string(),
  hint: z.string().optional(),
})

export const exerciseCodeSchema = z.object({
  type: z.literal('code'),
  question: z.string(),
  language: z.string().default('javascript'),
  starter: z.string().default(''),
})

export const exerciseFillSchema = z.object({
  type: z.literal('fill'),
  question: z.string(),
  template: z.string(),
  answers: z.array(z.string()),
  explanation: z.string().optional(),
})

export const exerciseDataSchema = z.discriminatedUnion('type', [
  exerciseMcqSchema,
  exerciseOpenSchema,
  exerciseCodeSchema,
  exerciseFillSchema,
])

export type ExerciseMcq = z.infer<typeof exerciseMcqSchema>
export type ExerciseOpen = z.infer<typeof exerciseOpenSchema>
export type ExerciseCode = z.infer<typeof exerciseCodeSchema>
export type ExerciseFill = z.infer<typeof exerciseFillSchema>
export type ExerciseData = z.infer<typeof exerciseDataSchema>

// ─── Chart schemas ────────────────────────────────────────────────────────────

export const chartDatasetSchema = z.object({
  label: z.string().optional(),
  data: z.array(z.number()),
  color: z.string().optional(),
})

export const chartDataSchema = z.object({
  type: z.enum(['bar', 'line', 'pie', 'area']),
  title: z.string().optional(),
  labels: z.array(z.string()),
  datasets: z.array(chartDatasetSchema).min(1),
  unit: z.string().optional(),
})

export type ChartDataset = z.infer<typeof chartDatasetSchema>
export type ChartData = z.infer<typeof chartDataSchema>

/** Document processing status returned by GET /courses/:courseId/documents and status endpoint */
export interface CourseDocument {
  id: string
  filename: string
  status: 'pending' | 'processing' | 'ready' | 'failed'
  chunks_count: number | null
  mime_type: string
  size_bytes: number
  processed_at: string | null
}
